import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, clearAuth, getToken, messageFromBody } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Markdown } from '../components/Markdown';
import { useAuth } from '../lib/auth';

interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
  /** done olayından — öneri onay/red uç noktaları için. */
  runId?: string;
  pendingApprovals?: PendingApproval[];
  /** Bu oturumda karar verilen öneriler (proposalId → sonuç). */
  decisions?: Record<string, ProposalDecision>;
  traces?: AgentTrace[];
  /** Stream sırasında — sadece UI durum göstergesi için */
  streaming?: boolean;
  /** Aktif tool çağrısı (eşzamanlı görünür) */
  activeTool?: { name: string; startedAt: number } | null;
  ts: number;
}

interface PendingApproval {
  proposalId: string;
  tool: string;
  payload: unknown;
  summary: string;
}

type ProposalDecision = { kind: 'approved'; capaNumber: string } | { kind: 'rejected' };

interface AgentTrace {
  iteration: number;
  type: 'llm' | 'tool';
  toolName?: string;
  toolArgs?: unknown;
  durationMs: number;
  ok?: boolean;
  summary?: string;
}

type StreamEvent =
  | { type: 'start'; runId: string }
  | { type: 'text'; delta: string }
  | { type: 'tool_start'; iteration: number; name: string; args: unknown }
  | { type: 'tool_result'; iteration: number; name: string; ok: boolean; summary: string; durationMs: number }
  | { type: 'iter_end'; iteration: number }
  | { type: 'done'; runId: string; answer: string; traces: AgentTrace[]; pendingApprovals: PendingApproval[] }
  | { type: 'cancelled'; runId: string }
  | { type: 'error'; message: string };

const SUGGESTED_QUERIES = [
  '§8.4 Tedarikçi Yönetimi için boşluk taraması yap',
  'SE-PR-08 NCR karantina prosedürü ne diyor?',
  '§7.2 Yetkinlik için kritik sorulara hızlı bak',
  'Son 3 ayda en çok hangi parçada uygunsuzluk çıktı?',
];

export function AgentChatPage() {
  const { hasPermission } = useAuth();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  async function send(text: string) {
    const userMsg: AgentMessage = { role: 'user', content: text, ts: Date.now() };
    const initialAsst: AgentMessage = {
      role: 'assistant',
      content: '',
      ts: Date.now(),
      streaming: true,
      traces: [],
      activeTool: null,
    };
    setMessages((m) => [...m, userMsg, initialAsst]);
    setBusy(true);
    setError(null);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const token = getToken();
      const res = await fetch('/v1/agent/chat/stream', {
        method: 'POST',
        signal: ac.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok || !res.body) {
        // Gövde okunamasa (bağlantı koptu, iptal) bile oturum düşsün diye gövdeden önce.
        if (res.status === 401) clearAuth();
        // 503 (denetim izi yazılamadı) vb. problem gövdesi: ham JSON yerine başlığı göster.
        const raw = await res.text().catch(() => '');
        let body: unknown = raw;
        try {
          body = JSON.parse(raw);
        } catch {
          // düz metin gövde
        }
        throw new Error(messageFromBody(body, res.status));
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';

      const updateLast = (mutator: (m: AgentMessage) => AgentMessage) =>
        setMessages((arr) => {
          const out = arr.slice();
          const i = out.length - 1;
          if (i >= 0 && out[i]!.role === 'assistant') out[i] = mutator(out[i]!);
          return out;
        });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split('\n\n');
        buf = blocks.pop() ?? '';
        for (const block of blocks) {
          const dataLine = block
            .split('\n')
            .find((l) => l.startsWith('data: '));
          if (!dataLine) continue; // ping comment vb.
          const payload = dataLine.slice(6);
          let ev: StreamEvent;
          try {
            ev = JSON.parse(payload) as StreamEvent;
          } catch {
            continue;
          }
          if (ev.type === 'text') {
            updateLast((m) => ({ ...m, content: m.content + ev.delta }));
          } else if (ev.type === 'tool_start') {
            updateLast((m) => ({
              ...m,
              activeTool: { name: ev.name, startedAt: Date.now() },
            }));
          } else if (ev.type === 'tool_result') {
            updateLast((m) => ({
              ...m,
              activeTool: null,
              traces: [
                ...(m.traces ?? []),
                {
                  iteration: ev.iteration,
                  type: 'tool',
                  toolName: ev.name,
                  durationMs: ev.durationMs,
                  ok: ev.ok,
                  summary: ev.summary,
                },
              ],
            }));
          } else if (ev.type === 'done') {
            updateLast((m) => ({
              ...m,
              content: ev.answer || m.content,
              runId: ev.runId,
              traces: ev.traces,
              pendingApprovals: ev.pendingApprovals,
              streaming: false,
              activeTool: null,
            }));
          } else if (ev.type === 'error') {
            setError(ev.message);
            updateLast((m) => ({ ...m, streaming: false, activeTool: null }));
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Agent hatası');
      setMessages((arr) => {
        const out = arr.slice();
        const i = out.length - 1;
        if (i >= 0 && out[i]!.role === 'assistant') {
          out[i] = { ...out[i]!, streaming: false, activeTool: null };
        }
        return out;
      });
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    void send(text);
  }

  function cancel() {
    abortRef.current?.abort();
    setBusy(false);
  }

  /** Karar sunucuda verilir: CAPA izdeki öneriden açılır, karar denetim izine düşer. */
  async function decide(messageIndex: number, approval: PendingApproval, action: 'approve' | 'reject') {
    if (!hasPermission('capa.write')) {
      setError('Öneriye karar verme yetkiniz yok (capa.write gerekli).');
      return;
    }
    const runId = messages[messageIndex]?.runId;
    if (!runId) return;
    setApproving(`${messageIndex}-${approval.proposalId}`);
    setError(null);
    try {
      const path = `/v1/agent/runs/${runId}/proposals/${approval.proposalId}/${action}`;
      let decision: ProposalDecision;
      if (action === 'approve') {
        const res = await api.post<{ capa: { capaNumber: string } }>(path);
        decision = { kind: 'approved', capaNumber: res.capa.capaNumber };
      } else {
        await api.post(path);
        decision = { kind: 'rejected' };
      }
      setMessages((arr) =>
        arr.map((msg, i) =>
          i === messageIndex ? { ...msg, decisions: { ...msg.decisions, [approval.proposalId]: decision } } : msg,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Karar kaydedilemedi');
    } finally {
      setApproving(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="KYS Agent"
        subtitle="On-prem LLM + RAG (KYS dokümanları) + gap_scan tool. Streaming Markdown — token-by-token akış."
        clause="§7.5.3 (Kayıt kontrolü) — her tool çağrısı agent_traces'a loglanır"
      />

      <div className="card flex h-[78vh] flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                KYS asistanı bekliyor. Aşağıdaki sorulardan biriyle başlayabilirsiniz.
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {SUGGESTED_QUERIES.map((q) => (
                  <button
                    type="button"
                    key={q}
                    className="rounded-md border border-slate-200 px-3 py-2 text-left text-xs text-slate-700 hover:border-brand-300 hover:bg-brand-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-700 dark:hover:bg-brand-900/30"
                    onClick={() => {
                      setInput(q);
                      void send(q);
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  message={m}
                  onDecide={(a, action) => void decide(i, a, action)}
                  approvingKey={approving}
                  messageIndex={i}
                />
              ))}
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="border-t border-slate-200 p-3 dark:border-slate-800">
          {error && (
            <div className="mb-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
              {error}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              className="input min-h-[44px] resize-y"
              placeholder="Soru yazın… (Shift+Enter satır içi, Enter gönder)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (input.trim() && !busy) {
                    const t = input.trim();
                    setInput('');
                    void send(t);
                  }
                }
              }}
              rows={2}
              disabled={busy}
            />
            {busy ? (
              <button type="button" onClick={cancel} className="btn-danger">
                Durdur
              </button>
            ) : (
              <button type="submit" className="btn-primary" disabled={!input.trim()}>
                Gönder
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onDecide,
  approvingKey,
  messageIndex,
}: {
  message: AgentMessage;
  onDecide: (a: PendingApproval, action: 'approve' | 'reject') => void;
  approvingKey: string | null;
  messageIndex: number;
}) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={
          isUser
            ? 'max-w-2xl rounded-lg bg-brand-600 px-4 py-3 text-sm text-white dark:bg-brand-500'
            : 'max-w-3xl rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-900 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700'
        }
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</div>
        ) : message.content ? (
          <Markdown>{message.content}</Markdown>
        ) : (
          <div className="text-xs italic text-slate-500 dark:text-slate-400">Düşünüyor…</div>
        )}

        {message.streaming && (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
            {message.activeTool ? (
              <span>
                <span className="font-mono">{message.activeTool.name}</span> çalışıyor…
              </span>
            ) : (
              <span>akıyor…</span>
            )}
          </div>
        )}

        {!message.streaming && message.traces && message.traces.length > 0 && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer opacity-70">
              {message.traces.length} adım ·{' '}
              {message.traces.filter((t) => t.type === 'tool').length} tool çağrısı
            </summary>
            <ul className="mt-2 space-y-1 text-xs opacity-80">
              {message.traces.map((t, i) => (
                <li key={i} className="font-mono">
                  {t.type === 'tool' ? (
                    <span>
                      → {t.toolName} ({t.durationMs}ms){' '}
                      {t.ok === false && <span className="text-rose-500">✗</span>}
                      {t.summary && (
                        <span className="ml-1 opacity-70">— {t.summary.slice(0, 100)}</span>
                      )}
                    </span>
                  ) : (
                    <span>llm yanıt ({t.durationMs}ms)</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {!message.streaming && message.pendingApprovals && message.pendingApprovals.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.pendingApprovals.map((a) => {
              const key = `${messageIndex}-${a.proposalId}`;
              const decision = message.decisions?.[a.proposalId];
              if (decision) {
                return (
                  <div
                    key={key}
                    className={
                      decision.kind === 'approved'
                        ? 'rounded-md bg-emerald-50 p-3 text-xs text-emerald-900 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900'
                        : 'rounded-md bg-slate-50 p-3 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-300 dark:ring-slate-700'
                    }
                  >
                    {decision.kind === 'approved' ? `✓ ${decision.capaNumber} açıldı` : '✗ Reddedildi'}
                  </div>
                );
              }
              return (
                <div
                  key={key}
                  className="rounded-md bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900"
                >
                  <div className="font-medium">⚠ Onay bekliyor — {a.tool}</div>
                  <div className="mt-1">{a.summary}</div>
                  <details className="mt-1">
                    <summary className="cursor-pointer opacity-70">Taslak payload</summary>
                    <pre className="mt-1 overflow-x-auto rounded bg-amber-100 p-2 font-mono text-[11px] dark:bg-amber-900/40">
                      {JSON.stringify(a.payload, null, 2)}
                    </pre>
                  </details>
                  {a.tool === 'draft_capa' && (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        className="btn-primary text-xs"
                        disabled={approvingKey === key}
                        onClick={() => onDecide(a, 'approve')}
                      >
                        {approvingKey === key ? 'Kaydediliyor…' : 'CAPA Olarak Aç'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        disabled={approvingKey === key}
                        onClick={() => onDecide(a, 'reject')}
                      >
                        Reddet
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
